import { CameraControls, CameraControlsImpl } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import type React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  type Box3,
  type Group,
  PerspectiveCamera,
  Raycaster,
  Spherical,
  Vector2,
  Vector3,
} from "three";
import { useProductStoreContext } from "./ProductStoreContext";
import {
  type CameraAutoFit,
  type CameraAutoFitTrigger,
  CameraControlsMode,
  useProductViewerConfig,
} from "./ProductViewerConfigContext";
import {
  advancePendingFit,
  advanceStableBoundsTracker,
  cameraAnglesToViewDirection,
  cancelPendingFits,
  computeProductBoundingBox,
  createPendingFitState,
  enqueuePendingFit,
  fitPerspectiveCameraToBox,
  hasPendingFit,
  hasPendingSceneAssets,
  ISOMETRIC_CAMERA_DIRECTION,
  readCameraAngles,
  type StableBoundsTracker,
  settlePendingFit,
  takePendingFit,
  USER_CANCELLABLE_FIT_REASONS,
  VISIBLE_PRODUCT_BOUNDS,
  waitForNextAnimationFrame,
} from "./productViewerCameraFit";
import {
  depthInFront,
  MIN_ORBIT_DISTANCE,
  pickProductSurfacePoint,
  registerPointerDown,
  resolveDollyPivot,
  setOrbitPointAtSurface,
  shouldPressSetPivot,
  shouldRepickWheelPivot,
  type WheelPickState,
  zoomButtonDistance,
} from "./productViewerCameraInteraction";
import { CameraView } from "./useProductViewer";
import { trackUserCameraControl } from "./userCameraControl";

const CAMERA_TRANSITION_SMOOTH_TIME = 0.25;
/**
 * Lets the camera glide between sparse pointer events of a slow orbit instead
 * of jumping on each one, without a noticeable lag behind the pointer.
 */
const DRAGGING_SMOOTH_TIME = 0.06;

type CameraDirection = [number, number, number];

const CAMERA_VIEW_DIRECTION: Record<CameraView, CameraDirection> = {
  [CameraView.FRONT]: [0, 0, 2],
  [CameraView.BACK]: [0, 0, -2],
  [CameraView.TOP]: [0, 2, 0],
  [CameraView.BOTTOM]: [0, -2, 0],
  [CameraView.LEFT]: [-2, 0, 0],
  [CameraView.RIGHT]: [2, 0, 0],
  [CameraView.ISOMETRIC]: ISOMETRIC_CAMERA_DIRECTION,
};

const FREE_ORBIT_MOUSE_BUTTONS = {
  left: CameraControlsImpl.ACTION.ROTATE,
  middle: CameraControlsImpl.ACTION.DOLLY,
  right: CameraControlsImpl.ACTION.TRUCK,
  wheel: CameraControlsImpl.ACTION.DOLLY,
} as const;

const FIXED_ORBIT_MOUSE_BUTTONS = {
  left: CameraControlsImpl.ACTION.ROTATE,
  middle: CameraControlsImpl.ACTION.DOLLY,
  right: CameraControlsImpl.ACTION.NONE,
  wheel: CameraControlsImpl.ACTION.DOLLY,
} as const;

const FREE_ORBIT_TOUCHES = {
  one: CameraControlsImpl.ACTION.TOUCH_ROTATE,
  two: CameraControlsImpl.ACTION.TOUCH_DOLLY_TRUCK,
  three: CameraControlsImpl.ACTION.TOUCH_TRUCK,
} as const;

const FIXED_ORBIT_TOUCHES = {
  one: CameraControlsImpl.ACTION.TOUCH_ROTATE,
  two: CameraControlsImpl.ACTION.TOUCH_DOLLY,
  three: CameraControlsImpl.ACTION.NONE,
} as const;

type WritableRef<T> = {
  current: T;
};

const resetBoundsTracking = (
  stableFrameCountRef: WritableRef<number>,
  lastBoxRef: WritableRef<Box3 | null>,
) => {
  stableFrameCountRef.current = 0;
  lastBoxRef.current = null;
};

const hasStableBounds = (
  currentBox: Box3,
  stableFrameCountRef: WritableRef<number>,
  lastBoxRef: WritableRef<Box3 | null>,
) => {
  const tracker: StableBoundsTracker = {
    stableFrameCount: stableFrameCountRef.current,
    lastBox: lastBoxRef.current,
  };
  const ready = advanceStableBoundsTracker(currentBox, tracker);
  stableFrameCountRef.current = tracker.stableFrameCount;
  lastBoxRef.current = tracker.lastBox;
  return ready;
};

/**
 * Advances the per-frame bounds-stability tracker and returns true once the
 * product bounds are non-empty and have stayed stable for
 * INITIAL_FIT_STABLE_FRAMES consecutive frames. Mutates the tracker refs, so
 * it must be called exactly once per frame for a given fit trigger to avoid
 * fitting the camera against a transient box while geometry is still settling.
 */
const advanceFitReadiness = (
  mesh: Group,
  stableFrameCountRef: WritableRef<number>,
  lastBoxRef: WritableRef<Box3 | null>,
): boolean => {
  const currentBox = computeProductBoundingBox(
    mesh,
    undefined,
    VISIBLE_PRODUCT_BOUNDS,
  );
  if (currentBox.isEmpty()) {
    resetBoundsTracking(stableFrameCountRef, lastBoxRef);
    return false;
  }

  return hasStableBounds(currentBox, stableFrameCountRef, lastBoxRef);
};

const hasCameraAutoFitTrigger = (
  cameraAutoFit: CameraAutoFit,
  trigger: CameraAutoFitTrigger,
) => cameraAutoFit !== false && cameraAutoFit.includes(trigger);

const _zoomSpherical = new Spherical();

/**
 * One zoom button press: a fixed share of the orbit distance. Scales the end
 * value, so presses in quick succession compound instead of restarting from
 * wherever the previous transition has got to.
 */
const zoomByButton = (
  controls: CameraControls | null,
  direction: "in" | "out",
) => {
  if (!controls) return;
  const distance = controls.getSpherical(_zoomSpherical, true).radius;
  void controls.dollyTo(
    zoomButtonDistance(distance, direction, controls),
    true,
  );
};

/**
 * The element camera-controls listens on: drei connects it to the canvas'
 * event source and falls back to the canvas.
 */
const useControlsElement = (): HTMLElement => {
  const connected = useThree((state) => state.events.connected) as
    | HTMLElement
    | null
    | undefined;
  const canvas = useThree((state) => state.gl.domElement);
  return connected ?? canvas;
};

/**
 * Free orbit turns and zooms around the product surface under the pointer.
 *
 * Both listeners run in the capture phase on the controls' own element, so
 * the orbit point is in place before camera-controls starts the rotation or
 * the dolly the same event triggers, and before react-three-fiber hands the
 * press to the parts, whose handlers may stop it.
 */
const useSurfaceOrbitPoint = ({
  enabled,
  controls,
  productRef,
}: {
  enabled: boolean;
  controls: CameraControls | null;
  productRef: { readonly current: Group | null };
}) => {
  const camera = useThree((state) => state.camera);
  const element = useControlsElement();

  useEffect(() => {
    if (!enabled || !controls) return;
    const raycaster = new Raycaster();
    const pointer = new Vector2();
    const cameraDirection = new Vector3();
    const activePointers = new Map<number, string>();
    let lastWheelPick: WheelPickState | null = null;
    let wheelPivot: Vector3 | null = null;

    const castRay = (clientX: number, clientY: number): boolean => {
      const rect = element.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return false;
      pointer.set(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1,
      );
      camera.updateMatrixWorld();
      raycaster.setFromCamera(pointer, camera);
      return true;
    };

    const handlePointerDown = (event: PointerEvent) => {
      // Whatever the press does next, orbit, pan or drag, moves the camera or
      // the parts away from the last wheel pick.
      lastWheelPick = null;
      const otherActivePointers = registerPointerDown(activePointers, event);

      const product = productRef.current;
      if (!product) return;
      if (
        !shouldPressSetPivot({
          button: event.button,
          isPrimary: event.isPrimary,
          metaKey: event.metaKey,
          ctrlKey: event.ctrlKey,
          otherActivePointers,
          freeOrbit: true,
          controlsEnabled: controls.enabled,
        })
      ) {
        return;
      }
      if (!castRay(event.clientX, event.clientY)) return;
      const hit = pickProductSurfacePoint(product, raycaster);
      if (hit) setOrbitPointAtSurface(controls, hit);
    };

    const handlePointerEnd = (event: PointerEvent) => {
      activePointers.delete(event.pointerId);
    };

    // A pointer released while the window had no focus never reports its
    // pointerup here; a blur ends every gesture anyway.
    const handleBlur = () => activePointers.clear();

    const handleWheel = (event: WheelEvent) => {
      const product = productRef.current;
      if (!controls.enabled || !product) return;
      if (!castRay(event.clientX, event.clientY)) return;
      const hit = pickProductSurfacePoint(product, raycaster);
      const hitDepth = hit ? depthInFront(camera, hit) : null;
      // A wheel notch never carries the camera through the nearest surface
      // under the cursor, whatever moved it: the cursor, a rebuild, a lid
      // swinging toward the camera. Hence the pick on every notch, even within
      // a burst that keeps its pivot. Inside the minimum distance an inward
      // notch is dropped; a surface in front of the pivot becomes the pivot.
      if (hitDepth !== null && hitDepth < controls.minDistance) {
        lastWheelPick = null;
        if (event.deltaY < 0) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
        return;
      }
      if (lastWheelPick && wheelPivot) {
        lastWheelPick.depth = depthInFront(camera, wheelPivot);
      }
      const sample = {
        clientX: event.clientX,
        clientY: event.clientY,
        timeStamp: event.timeStamp,
        depth: hitDepth,
      };
      if (!shouldRepickWheelPivot(lastWheelPick, sample)) {
        // Still the same burst: keep dollying toward the point it picked.
        if (lastWheelPick) lastWheelPick.timeStamp = sample.timeStamp;
        return;
      }
      lastWheelPick = sample;
      const pivot = resolveDollyPivot({
        hit,
        ray: raycaster.ray,
        productBounds: hit
          ? null
          : computeProductBoundingBox(
              product,
              undefined,
              VISIBLE_PRODUCT_BOUNDS,
            ),
        cameraDirection: camera.getWorldDirection(cameraDirection),
      });
      wheelPivot =
        pivot && setOrbitPointAtSurface(controls, pivot) ? pivot : null;
    };

    const ownerDocument = element.ownerDocument;
    element.addEventListener("pointerdown", handlePointerDown, true);
    // Not passive: a notch it drops must not scroll the page either.
    element.addEventListener("wheel", handleWheel, {
      capture: true,
      passive: false,
    });
    ownerDocument.addEventListener("pointerup", handlePointerEnd, true);
    ownerDocument.addEventListener("pointercancel", handlePointerEnd, true);
    const ownerWindow = ownerDocument.defaultView;
    ownerWindow?.addEventListener("blur", handleBlur);
    return () => {
      ownerWindow?.removeEventListener("blur", handleBlur);
      element.removeEventListener("pointerdown", handlePointerDown, true);
      element.removeEventListener("wheel", handleWheel, true);
      ownerDocument.removeEventListener("pointerup", handlePointerEnd, true);
      ownerDocument.removeEventListener(
        "pointercancel",
        handlePointerEnd,
        true,
      );
    };
  }, [enabled, element, camera, controls, productRef]);
};

interface CameraControlsContextValue {
  disableControls: () => (() => void) | undefined;
}

const CameraControlsContext = createContext<CameraControlsContextValue | null>(
  null,
);

export const useCameraControls = () => {
  const context = useContext(CameraControlsContext);
  return context;
};

interface ProductViewerCameraProps {
  children: React.ReactNode;
  onInitialFitComplete: () => void;
}

export const ProductViewerCamera = ({
  children,
  onInitialFitComplete,
}: ProductViewerCameraProps) => {
  const { cameraControls: cameraControlsMode, cameraAutoFit = false } =
    useProductViewerConfig();
  const meshRef = useRef<Group>(null);
  const cameraControlsRef = useRef<CameraControls>(null);
  const initialFitDoneRef = useRef(false);
  const stableBoundsFrameCountRef = useRef(0);
  const lastInitialFitBoxRef = useRef<Box3 | null>(null);
  const stableAutoFitBoundsFrameCountRef = useRef(0);
  const lastAutoFitBoxRef = useRef<Box3 | null>(null);
  const previousBuildVersionRef = useRef(0);
  const previousCanvasSizeRef = useRef<{
    height: number;
    width: number;
  } | null>(null);
  /**
   * Every reason the camera owes a fit, collapsed into one request. Fitting is
   * declarative: callers state where the camera should end up and the frame
   * loop converges on it once the scene holds still, so overlapping requests
   * (a step change during a lid animation, a rebuild during a view change)
   * settle into a single correct fit instead of racing each other.
   */
  const pendingFitRef = useRef(createPendingFitState());
  const useProductStore = useProductStoreContext();
  const setOnCameraViewChange = useProductStore(
    (state) => state.setOnCameraViewChange,
  );
  const setOnCameraFit = useProductStore((state) => state.setOnCameraFit);
  const setOnZoomIn = useProductStore((state) => state.setOnZoomIn);
  const setOnZoomOut = useProductStore((state) => state.setOnZoomOut);
  const buildVersion = useProductStore((state) => state.buildVersion);
  const clearCameraView = useProductStore((state) => state.clearCameraView);
  /**
   * The named view the camera should be showing, read imperatively so that
   * changing it never re-renders the whole product subtree.
   */
  const getRequestedViewDirection = useCallback((): CameraDirection | null => {
    const view = useProductStore.getState().cameraView;
    return view ? CAMERA_VIEW_DIRECTION[view] : null;
  }, [useProductStore]);

  const camera = useThree((state) => state.camera);
  const canvasWidth = useThree((state) => state.size.width);
  const canvasHeight = useThree((state) => state.size.height);
  const invalidate = useThree((state) => state.invalidate);

  const autoFitOnConfigChange = hasCameraAutoFitTrigger(
    cameraAutoFit,
    "configChange",
  );
  const autoFitOnCanvasResize = hasCameraAutoFitTrigger(
    cameraAutoFit,
    "canvasResize",
  );

  const isFreeOrbit = cameraControlsMode === CameraControlsMode.FreeOrbit;
  const userInteractionEnabled = cameraControlsMode !== CameraControlsMode.None;

  /**
   * drei builds a new camera-controls instance whenever the camera changes.
   * Everything that listens on the instance is keyed on this state, so no
   * listener stays behind on an instance that no longer moves the camera.
   */
  const [controls, setControls] = useState<CameraControls | null>(null);

  /**
   * The controls are enabled while the config allows user interaction and no
   * caller holds them disabled (a part drag). Every writer goes through
   * `applyControlsEnabled`, so neither a re-render, a config change nor a new
   * instance re-enables the controls under a drag.
   */
  const userInteractionEnabledRef = useRef(userInteractionEnabled);
  const controlsDisableCountRef = useRef(0);
  const applyControlsEnabled = useCallback(() => {
    const instance = cameraControlsRef.current;
    if (!instance) return;
    instance.enabled =
      userInteractionEnabledRef.current &&
      controlsDisableCountRef.current === 0;
  }, []);

  // Stable, so React hands it the instance only when the instance changes.
  const handleControlsRef = useCallback(
    (instance: CameraControls | null) => {
      cameraControlsRef.current = instance;
      applyControlsEnabled();
      setControls(instance);
    },
    [applyControlsEnabled],
  );

  useLayoutEffect(() => {
    userInteractionEnabledRef.current = userInteractionEnabled;
    applyControlsEnabled();
  }, [userInteractionEnabled, applyControlsEnabled]);

  const disableControls = useCallback(() => {
    controlsDisableCountRef.current += 1;
    applyControlsEnabled();
    let released = false;
    return () => {
      if (released) return;
      released = true;
      controlsDisableCountRef.current -= 1;
      applyControlsEnabled();
    };
  }, [applyControlsEnabled]);

  // Publishes "the user is controlling the camera" for the renderer, which
  // holds its motion quality and shadow cadence for the whole gesture, and
  // for the fit loop below, which holds automatic fits until the release.
  const userControlRef = useRef<ReturnType<
    typeof trackUserCameraControl
  > | null>(null);
  useEffect(() => {
    if (!controls) return;
    const { setUserControllingCamera } = useProductStore.getState();
    const tracker = trackUserCameraControl(controls, {
      onChange: setUserControllingCamera,
      invalidate,
      window: globalThis.window,
    });
    userControlRef.current = tracker;
    return () => {
      userControlRef.current = null;
      tracker.detach();
    };
  }, [controls, useProductStore, invalidate]);
  useFrame(() => userControlRef.current?.frame());

  // Preview capture renders the product from the angle the user looks at.
  useEffect(
    () =>
      useProductStore.getState().provideCameraAngles(() => {
        const instance = cameraControlsRef.current;
        return instance ? readCameraAngles(instance) : null;
      }),
    [useProductStore],
  );

  useEffect(() => {
    const previousBuildVersion = previousBuildVersionRef.current;
    previousBuildVersionRef.current = buildVersion;

    if (
      !autoFitOnConfigChange ||
      buildVersion === 0 ||
      buildVersion === previousBuildVersion
    ) {
      return;
    }

    enqueuePendingFit(pendingFitRef.current, "configChange", Date.now());
    resetBoundsTracking(stableAutoFitBoundsFrameCountRef, lastAutoFitBoxRef);
  }, [autoFitOnConfigChange, buildVersion]);

  useEffect(() => {
    const previousCanvasSize = previousCanvasSizeRef.current;
    previousCanvasSizeRef.current = {
      height: canvasHeight,
      width: canvasWidth,
    };

    if (
      !autoFitOnCanvasResize ||
      !initialFitDoneRef.current ||
      !previousCanvasSize ||
      (previousCanvasSize.height === canvasHeight &&
        previousCanvasSize.width === canvasWidth)
    ) {
      return;
    }

    enqueuePendingFit(pendingFitRef.current, "canvasResize", Date.now());
    resetBoundsTracking(stableAutoFitBoundsFrameCountRef, lastAutoFitBoxRef);
  }, [autoFitOnCanvasResize, canvasHeight, canvasWidth]);

  const fitCurrentCameraView = useCallback(
    async (enableTransition = true) => {
      const instance = cameraControlsRef.current;
      if (
        !meshRef.current ||
        !instance ||
        !(camera instanceof PerspectiveCamera)
      ) {
        return;
      }

      // While a `setCameraView` transition is in flight the live camera points
      // somewhere between the two views, so sampling it here would park the
      // camera at that intermediate pose. Prefer the requested named view.
      const currentViewDirection =
        getRequestedViewDirection() ??
        cameraAnglesToViewDirection(readCameraAngles(instance));

      meshRef.current.updateMatrixWorld(true);
      await fitPerspectiveCameraToBox(
        instance,
        camera,
        meshRef.current,
        currentViewDirection,
        enableTransition,
      );
    },
    [camera, getRequestedViewDirection],
  );

  const completeInitialFit = useCallback(() => {
    void waitForNextAnimationFrame()
      .then(waitForNextAnimationFrame)
      .then(onInitialFitComplete);
  }, [onInitialFitComplete]);

  useFrame(() => {
    if (!(camera instanceof PerspectiveCamera)) return;
    if (!cameraControlsRef.current || !meshRef.current) return;

    const needsInitialFit = !initialFitDoneRef.current;
    const pendingFit = pendingFitRef.current;

    if (!needsInitialFit && !hasPendingFit(pendingFit)) {
      return;
    }
    meshRef.current.updateMatrixWorld(true);
    const hasPendingAssets = hasPendingSceneAssets(meshRef.current);

    if (needsInitialFit && hasPendingAssets) {
      resetBoundsTracking(stableBoundsFrameCountRef, lastInitialFitBoxRef);
      return;
    }

    if (needsInitialFit) {
      if (
        !advanceFitReadiness(
          meshRef.current,
          stableBoundsFrameCountRef,
          lastInitialFitBoxRef,
        )
      ) {
        if (stableBoundsFrameCountRef.current > 0) invalidate();
        return;
      }

      initialFitDoneRef.current = true;

      void fitPerspectiveCameraToBox(
        cameraControlsRef.current,
        camera,
        meshRef.current,
        getRequestedViewDirection() ?? ISOMETRIC_CAMERA_DIRECTION,
        false,
      ).then(() => {
        invalidate();
        // Anything requested while the viewer was starting up stays pending on
        // purpose: this fit can bail (e.g. on bounds that are still empty), and
        // treating it as "the request has been served" would drop the request
        // on the floor. A pending one simply fits again on a later frame.
        completeInitialFit();
      });
      return;
    }

    const { fitHoldCount: holdCount, isUserControllingCamera } =
      useProductStore.getState();
    // Advancing the tracker mutates it, so it must happen exactly once per
    // frame - but only while nothing else already forces us to wait, so that a
    // scene which is still loading or being prepared cannot bank "stable"
    // frames it did not earn.
    const blocked =
      hasPendingAssets || holdCount > 0 || isUserControllingCamera;
    if (blocked) {
      resetBoundsTracking(stableAutoFitBoundsFrameCountRef, lastAutoFitBoxRef);
    }
    const boundsStable =
      !blocked &&
      advanceFitReadiness(
        meshRef.current,
        stableAutoFitBoundsFrameCountRef,
        lastAutoFitBoxRef,
      );

    const decision = advancePendingFit(pendingFit, {
      boundsStable,
      hasPendingAssets,
      holdCount,
      userControllingCamera: isUserControllingCamera,
      now: Date.now(),
    });

    if (decision !== "fit" && decision !== "forceFit") {
      // Under the demand loop nothing else asks for the frame this wait needs:
      // without it neither the bounds tracker nor the request's own timeout
      // would ever be looked at again, and the fit would be stranded. The loop
      // cannot spin on this, because the timeout forces the fit either way.
      // A hold needs no frame: the release of the camera requests one.
      if (decision === "wait") invalidate();
      return;
    }

    // Clear the request before fitting: the fit is asynchronous, and leaving it
    // pending would re-enter it on every frame of its own transition.
    const resolvers = takePendingFit(pendingFit);
    resetBoundsTracking(stableAutoFitBoundsFrameCountRef, lastAutoFitBoxRef);
    void fitCurrentCameraView().then(() => {
      for (const resolve of resolvers) {
        resolve();
      }
    });
  });

  useEffect(() => {
    if (!controls) return;
    const handleChange = () => {
      // camera-controls dispatches "control" only from its pointer/wheel
      // handlers, never from a programmatic setLookAt transition, so this is
      // exactly "the user took over". Drop the fits that were framing a view
      // for them; automatic re-fits after a rebuild or resize stay pending.
      clearCameraView();
      cancelPendingFits(pendingFitRef.current, USER_CANCELLABLE_FIT_REASONS);
    };

    controls.addEventListener("control", handleChange);
    return () => controls.removeEventListener("control", handleChange);
  }, [controls, clearCameraView]);

  useEffect(() => {
    // Requesting a view records the target and lets the frame loop converge on
    // it once the scene holds still. Fitting straight away would frame the
    // product mid-rebuild (empty bounds - the fit silently did nothing) or
    // mid-animation (bounds it is about to leave).
    //
    // The frame loop only runs on demand, so the request has to ask for a
    // frame itself: on a camera at rest nothing else would render one, and
    // the fit would sit in the queue until the next unrelated frame - a zoom,
    // say, which it would then undo.
    const requestFit = (reason: "viewChange" | "manual") =>
      new Promise<void>((resolve) => {
        enqueuePendingFit(pendingFitRef.current, reason, Date.now(), resolve);
        resetBoundsTracking(
          stableAutoFitBoundsFrameCountRef,
          lastAutoFitBoxRef,
        );
        invalidate();
      });

    setOnCameraViewChange(() => requestFit("viewChange"));

    setOnCameraFit(() => requestFit("manual"));

    setOnZoomIn(() => zoomByButton(cameraControlsRef.current, "in"));

    setOnZoomOut(() => zoomByButton(cameraControlsRef.current, "out"));

    const pendingFit = pendingFitRef.current;
    return () => {
      // Nothing will fit once this viewer is gone, so release the callers
      // waiting on it rather than leaving their promises unsettled.
      settlePendingFit(pendingFit);
    };
  }, [
    invalidate,
    setOnCameraViewChange,
    setOnCameraFit,
    setOnZoomIn,
    setOnZoomOut,
  ]);

  useSurfaceOrbitPoint({
    enabled: isFreeOrbit,
    controls,
    productRef: meshRef,
  });

  const contextValue = useMemo(() => ({ disableControls }), [disableControls]);

  return (
    <CameraControlsContext.Provider value={contextValue}>
      <CameraControls
        ref={handleControlsRef}
        mouseButtons={
          isFreeOrbit ? FREE_ORBIT_MOUSE_BUTTONS : FIXED_ORBIT_MOUSE_BUTTONS
        }
        touches={isFreeOrbit ? FREE_ORBIT_TOUCHES : FIXED_ORBIT_TOUCHES}
        dollyToCursor={isFreeOrbit}
        smoothTime={CAMERA_TRANSITION_SMOOTH_TIME}
        draggingSmoothTime={DRAGGING_SMOOTH_TIME}
        dollySpeed={0.3}
        minDistance={MIN_ORBIT_DISTANCE}
      />
      <group ref={meshRef} visible={true}>
        {children}
      </group>
    </CameraControlsContext.Provider>
  );
};
